// src/Pages/PhoneCards.js

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import logo from "../assets/rcslogo.png";
import api from "../api";

import "../Styles/PhoneCards.css";

import {
  Smartphone,
  Search,
  Plus,
  Wallet,
  TrendingUp,
  Boxes,
  CheckCircle2,
  Trash2,
  Pencil,
  X,
  Package,
} from "lucide-react";

import { toast } from "react-toastify";

/* ========================================
    HELPERS
======================================== */

const formatMoney = (n) =>
  new Intl.NumberFormat(
    "en-US",
    {
      style: "currency",
      currency: "GYD",
    }
  ).format(Number(n || 0));

const formatDate = (d) =>
  new Date(d).toLocaleDateString();

/* ========================================
    MAIN
======================================== */

const PhoneCards = () => {

  /* ========================================
      STATE
  ======================================== */

  const [rows, setRows] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [search, setSearch] =
    useState("");

  const [showModal, setShowModal] =
    useState(false);

  const [form, setForm] =
    useState({
      date:
        new Date()
          .toISOString()
          .slice(0, 10),

      type: "GTT",

      value: "",

      startCode: "",

      endCode: "",

      status: "available",
    });

  /* ========================================
      FETCH
  ======================================== */

  const fetchData = async () => {

    try {

      setLoading(true);

      const data =
        await api.getPhoneCards({
          search,
        });

      setRows(data || []);

    } catch (error) {

      console.error(error);

      toast.error(
        "Failed to load phone cards."
      );

    } finally {

      setLoading(false);

    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {

    const timeout =
      setTimeout(() => {
        fetchData();
      }, 300);

    return () =>
      clearTimeout(timeout);

  }, [search]);

  /* ========================================
      CREATE
  ======================================== */

  const createPack =
    async () => {

      try {

        if (
          !form.date ||
          !form.type ||
          !form.value ||
          !form.startCode ||
          !form.endCode
        ) {

          toast.warn(
            "Complete all fields."
          );

          return;
        }

        const payload = {
          date: form.date,

          type: form.type,

          value:
            Number(form.value),

          startCode:
            form.startCode,

          endCode:
            form.endCode,

          status:
            form.status,
        };

        const created =
          await api.createPhoneCard(
            payload
          );

        if (created) {

          toast.success(
            "Pack created."
          );

          setShowModal(false);

          setForm({
            date:
              new Date()
                .toISOString()
                .slice(0, 10),

            type: "GTT",

            value: "",

            startCode: "",

            endCode: "",

            status:
              "available",
          });

          fetchData();

        }

      } catch (error) {

        console.error(error);

        toast.error(
          "Failed to create pack."
        );

      }
    };

  /* ========================================
      TOGGLE
  ======================================== */

  const toggleStatus =
    async (
      id,
      current
    ) => {

      try {

        const next =
          current === "sold"
            ? "available"
            : "sold";

        const updated =
          await api.setPhoneCardStatus(
            id,
            next
          );

        if (updated) {

          toast.success(
            "Status updated."
          );

          fetchData();

        }

      } catch (error) {

        console.error(error);

        toast.error(
          "Update failed."
        );

      }
    };

  /* ========================================
      DELETE
  ======================================== */

  const handleDelete =
    async (id) => {

      if (
        !window.confirm(
          "Delete this pack?"
        )
      )
        return;

      try {

        const success =
          await api.deletePhoneCard(
            id
          );

        if (success) {

          toast.success(
            "Pack deleted."
          );

          fetchData();

        }

      } catch (error) {

        console.error(error);

        toast.error(
          "Delete failed."
        );

      }
    };

  /* ========================================
      NORMALIZED
  ======================================== */

  const normalizedRows =
    useMemo(() => {

      return rows.map((r) => ({
        id: r.id,

        date: r.date,

        type: r.type,

        value: Number(
          r.value || 0
        ),

        startCode:
          r.start_code ??
          r.startCode,

        endCode:
          r.end_code ??
          r.endCode,

        status: r.status,

        statusChangedAt:
          r.status_changed_at ??
          r.statusChangedAt,
      }));

    }, [rows]);

  /* ========================================
      STATS
  ======================================== */

  const totalInventory =
    normalizedRows.reduce(
      (sum, r) =>
        sum + r.value,
      0
    );

  const soldInventory =
    normalizedRows
      .filter(
        (r) =>
          r.status ===
          "sold"
      )
      .reduce(
        (sum, r) =>
          sum + r.value,
        0
      );

  const availableInventory =
    normalizedRows
      .filter(
        (r) =>
          r.status ===
          "available"
      )
      .reduce(
        (sum, r) =>
          sum + r.value,
        0
      );

  const totalPacks =
    normalizedRows.length;

  /* ========================================
      BY TYPE
  ======================================== */

  const packsByType =
    useMemo(() => {

      const map =
        new Map();

      normalizedRows.forEach(
        (r) => {

          if (
            r.status !==
            "available"
          )
            return;

          const prev =
            map.get(r.type) || {
              type: r.type,

              count: 0,

              total: 0,
            };

          prev.count += 1;

          prev.total +=
            r.value;

          map.set(
            r.type,
            prev
          );
        }
      );

      return Array.from(
        map.values()
      );

    }, [normalizedRows]);

  /* ========================================
      LOADING
  ======================================== */

if (loading) {

  return (

    <div className="phonecards-modern-loading">

      <div className="phonecards-modern-loader">

        <div className="phonecards-modern-ring" />

        <div className="phonecards-modern-ring-two" />

        <div className="phonecards-modern-loader-center">

          <img
            src={logo}
            alt="RCS Logo"
            className="phonecards-modern-loader-logo"
          />

        </div>

      </div>

      <h2>
        Loading Inventory
      </h2>

      <p>
        Preparing inventory,
        balances and phone
        card activity...
      </p>

    </div>
  );
}
  /* ========================================
      UI
  ======================================== */

  return (

    <div className="phonecards-modern-page">

      {/* HERO */}

      <div className="phonecards-modern-hero">

        <div className="phonecards-modern-pill">

          <Smartphone size={14} />

          PHONE CARD INVENTORY

        </div>

        <h1>
          Inventory Control
        </h1>

        <p>
          Track available packs,
          sold inventory, card
          ranges and real-time
          phone card activity.
        </p>

        <div className="phonecards-modern-actions">

          <button
            className="phonecards-modern-primary-btn"
            onClick={() =>
              setShowModal(true)
            }
          >

            <Plus size={18} />

            New Pack

          </button>

        </div>

      </div>

      {/* STATS */}

      <div className="phonecards-modern-stats">

        <div className="phonecards-modern-stat-card teal">

          <div className="phonecards-modern-stat-icon">

            <Wallet size={22} />

          </div>

          <span>
            Total Inventory
          </span>

          <h2>
            {formatMoney(
              totalInventory
            )}
          </h2>

        </div>

        <div className="phonecards-modern-stat-card green">

          <div className="phonecards-modern-stat-icon">

            <CheckCircle2
              size={22}
            />

          </div>

          <span>
            Sold Inventory
          </span>

          <h2>
            {formatMoney(
              soldInventory
            )}
          </h2>

        </div>

        <div className="phonecards-modern-stat-card blue">

          <div className="phonecards-modern-stat-icon">

            <TrendingUp
              size={22}
            />

          </div>

          <span>
            Available Inventory
          </span>

          <h2>
            {formatMoney(
              availableInventory
            )}
          </h2>

        </div>

        <div className="phonecards-modern-stat-card orange">

          <div className="phonecards-modern-stat-icon">

            <Boxes size={22} />

          </div>

          <span>
            Total Packs
          </span>

          <h2>
            {totalPacks}
          </h2>

        </div>

      </div>

      {/* MAIN GRID */}

      <div className="phonecards-modern-grid">

        {/* LEFT */}

        <div className="phonecards-modern-panel">

          <div className="phonecards-modern-panel-header">

            <h2>
              Available Packs
            </h2>

          </div>

          <div className="phonecards-modern-pack-list">

            {packsByType.length ===
            0 ? (

              <div className="phonecards-modern-empty">

                <Package size={34} />

                <p>
                  No available
                  packs.
                </p>

              </div>

            ) : (

              packsByType.map(
                (pack) => (

                  <div
                    key={pack.type}
                    className="phonecards-modern-pack-card"
                  >

                    <div>

                      <h3>
                        {
                          pack.type
                        }
                      </h3>

                      <span>
                        {
                          pack.count
                        }{" "}
                        Packs
                      </span>

                    </div>

                    <strong>
                      {formatMoney(
                        pack.total
                      )}
                    </strong>

                  </div>
                )
              )

            )}

          </div>

        </div>

        {/* RIGHT */}

        <div className="phonecards-modern-panel">

          <div className="phonecards-modern-panel-header">

            <h2>
              Search Inventory
            </h2>

          </div>

          <div className="phonecards-modern-search">

            <Search size={18} />

            <input
              type="text"
              placeholder="Search by code..."
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
            />

          </div>

        </div>

      </div>

      {/* TABLE */}

      <div className="phonecards-modern-table-wrapper">

        <div className="phonecards-modern-table-head">

          <span>
            Date
          </span>

          <span>
            Type
          </span>

          <span>
            Value
          </span>

          <span>
            Start Code
          </span>

          <span>
            End Code
          </span>

          <span>
            Status
          </span>

          <span>
            Actions
          </span>

        </div>

        {normalizedRows.length ===
        0 ? (

          <div className="phonecards-modern-empty-table">

            <div className="phonecards-modern-empty-icon">

              <Search size={40} />

            </div>

            <h3>
              No packs found
            </h3>

            <p>
              Try another search
              or create a new
              inventory pack.
            </p>

          </div>

        ) : (

          normalizedRows.map(
            (row) => (

              <div
                key={row.id}
                className="phonecards-modern-row"
              >

                <div>
                  {formatDate(
                    row.date
                  )}
                </div>

                <div className="phonecards-modern-type">

                  {row.type}

                </div>

                <div className="phonecards-modern-value">

                  {formatMoney(
                    row.value
                  )}

                </div>

                <div className="phonecards-modern-code">

                  {row.startCode}

                </div>

                <div className="phonecards-modern-code">

                  {row.endCode}

                </div>

                <div>

                  <button
                    className={`phonecards-modern-status ${
                      row.status ===
                      "sold"
                        ? "sold"
                        : "available"
                    }`}
                    onClick={() =>
                      toggleStatus(
                        row.id,
                        row.status
                      )
                    }
                  >

                    {row.status}

                  </button>

                </div>

                <div className="phonecards-modern-actions-row">

                  <button className="edit">

                    <Pencil
                      size={16}
                    />

                  </button>

                  <button
                    className="delete"
                    onClick={() =>
                      handleDelete(
                        row.id
                      )
                    }
                  >

                    <Trash2
                      size={16}
                    />

                  </button>

                </div>

              </div>
            )
          )

        )}

      </div>

      {/* MODAL */}

      {showModal && (

        <div className="phonecards-modern-modal-overlay">

          <div className="phonecards-modern-modal">

            <div className="phonecards-modern-modal-header">

              <h2>
                New Inventory Pack
              </h2>

              <button
                onClick={() =>
                  setShowModal(
                    false
                  )
                }
              >

                <X size={18} />

              </button>

            </div>

            <div className="phonecards-modern-form-grid">

              <input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm({
                    ...form,

                    date:
                      e.target
                        .value,
                  })
                }
              />

              <select
                value={form.type}
                onChange={(e) =>
                  setForm({
                    ...form,

                    type:
                      e.target
                        .value,
                  })
                }
              >

                <option>
                  GTT
                </option>

                <option>
                  Digicel
                </option>

                <option>
                  Other
                </option>

              </select>

              <input
                type="number"
                placeholder="Value"
                value={form.value}
                onChange={(e) =>
                  setForm({
                    ...form,

                    value:
                      e.target
                        .value,
                  })
                }
              />

              <input
                type="text"
                placeholder="Start Code"
                value={
                  form.startCode
                }
                onChange={(e) =>
                  setForm({
                    ...form,

                    startCode:
                      e.target
                        .value,
                  })
                }
              />

              <input
                type="text"
                placeholder="End Code"
                value={
                  form.endCode
                }
                onChange={(e) =>
                  setForm({
                    ...form,

                    endCode:
                      e.target
                        .value,
                  })
                }
              />

              <select
                value={form.status}
                onChange={(e) =>
                  setForm({
                    ...form,

                    status:
                      e.target
                        .value,
                  })
                }
              >

                <option value="available">
                  Available
                </option>

                <option value="sold">
                  Sold
                </option>

              </select>

            </div>

            <div className="phonecards-modern-modal-actions">

              <button
                className="save"
                onClick={
                  createPack
                }
              >
                Save Pack
              </button>

              <button
                className="cancel"
                onClick={() =>
                  setShowModal(
                    false
                  )
                }
              >
                Cancel
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};

export default PhoneCards;